import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';
import { AgentOrders, IAgent } from 'src/app/models/IAgent.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { AgentsService } from 'src/app/services/agents.service';
import { Log } from 'src/app/services/shared/log';

/**
 * AgenteList — orders of one agent.
 *
 * Two modes:
 *   - **standalone** (route `/agentsorders?token=...`): full page with
 *     its own sticky header. The token arrives via the query param.
 *   - **embedded** (used as a drawer inside `/accesso-funzionari`): no
 *     own sticky header (the parent drawer already has one), token
 *     comes through the `agent_token` `@Input`. Drives `embedded=true`
 *     when the parent passes the input — the template uses this to
 *     skip its own header chrome.
 *
 * Renders the order list as a modern table with the same 10 columns as
 * the legacy Kendo grid (Agente, N° Ordine, Preventivo, Riferimento,
 * Ragione sociale, Data ordine, Data evasione, Stato, DDT, Azioni).
 * Clicking anywhere on a row opens the order-details modal — the row
 * itself is the call-to-action, matching the legacy expectation that
 * "tapping a row shows the details".
 *
 * Two row actions:
 *   - 📦 "Dettagli ordine": opens the `<app-ordini-agente-dettaggli>`
 *     widget as a side drawer (preserved as-is — legacy widget, its
 *     own restyle is a separate scheduled task).
 *   - 📍 "Tracking spedizione": opens the
 *     `<app-accesso-funzionari-tracking>` widget similarly.
 *
 * Architectural notes:
 *  - The old code coordinated child modals via
 *    `OpenModalService.isAgentTrack` / `isAgentOrderB`. This component
 *    now owns its own modal state (`showTracking`, `showOrderDetails`)
 *    in local fields. `OpenModalService` calls have been removed from
 *    this component's surface entirely.
 *  - `IonAccordionGroup` (Ionic) dependency is gone — Phase 1 backlog
 *    says drop Ionic, this is one removed reference.
 *  - `GridComponent` (Kendo) + the `adjustGridHeight` `window:resize`
 *    workaround are gone. The new table is a CSS grid with internal
 *    scroll on the body.
 *  - `markForCheck()` after every async mutation (the same CD-quirk
 *    pattern we hit in /messaggi and /accounts).
 */
@Component({
  selector: 'app-agente-list',
  templateUrl: './agente-list.component.html',
  styleUrl: './agente-list.component.scss',
  standalone: false,
})


export class AgenteListComponent implements OnInit {

  private readonly agentsService = inject(AgentsService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Token passed by a parent component when this view is embedded as a
   * drawer (see `/accesso-funzionari`). When set, the component skips
   * reading from the route query params and runs in embedded mode.
   */
  @Input() agent_token = '';

  /**
   * Optional flag the parent can pass to ask for an alleviated layout
   * (no own sticky header). Auto-detected at boot from the presence of
   * `agent_token` if not passed explicitly.
   */
  @Input() embedded?: boolean;

  // ── Data ──────────────────────────────────────────────────────────
  orders: AgentOrders[] = [];
  selectedAgent: IAgent | null = null;

  // ── UI state ──────────────────────────────────────────────────────
  searchText = '';
  currentPage = 0;
  static readonly PAGE_SIZE = 30;

  // ── Modal / drawer state ─────────────────────────────────────────
  showTracking = false;
  trackingTrackId = '';
  trackingAgentId: string | number = '';

  showOrderDetails = false;
  detailsAgentId = '';
  detailsOrderId: number | string = '';
  /** Human-readable order number (e.g. "19359/00") shown in the
   *  modal title. Kept separate from `detailsOrderId` (the internal
   *  numeric primary key the widget passes to the API). */
  detailsOrderNumber = '';
  detailsGammaId: number | string = '';
  detailsAssignedDate = '';
  detailsStatusCod = 0;
  detailsTokenAgent = '';

  // ── Sort state ────────────────────────────────────────────────────
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  // ── Async flag ────────────────────────────────────────────────────
  loading = false;

  ngOnInit(): void {
    // Auto-detect embedded mode: if the parent passed an agent_token,
    // we're inside someone else's layout.
    if (this.embedded === undefined) {
      this.embedded = !!this.agent_token;
    }

    if (this.embedded && this.agent_token) {
      // Embedded mode — token already provided, load directly.
      this.loadOrders(this.agent_token);
    } 
    else {
      // Standalone mode — read token from query param.
      this.route.queryParams
        .pipe(
          filter((params) => !!params['token']),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((params) => {
          this.agent_token = params['token'];
          this.loadOrders(this.agent_token);
          this.cdr.detectChanges();
        });
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state.
  // ──────────────────────────────────────────────────────────────────

  get filteredOrders(): AgentOrders[] {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return this.orders;
    return this.orders.filter((o) => this.haystack(o).includes(q));
  }

  get pagedOrders(): AgentOrders[] {
    const sorted = this.sortField
      ? [...this.filteredOrders].sort((a, b) => {
          const rawA = (a as any)[this.sortField] ?? '';
          const rawB = (b as any)[this.sortField] ?? '';
          const va = rawA !== '' && !isNaN(Number(rawA)) ? Number(rawA) : rawA;
          const vb = rawB !== '' && !isNaN(Number(rawB)) ? Number(rawB) : rawB;
          const cmp = va < vb ? -1 : va > vb ? 1 : 0;
          return this.sortDir === 'asc' ? cmp : -cmp;
        })
      : this.filteredOrders;
    const start = this.currentPage * AgenteListComponent.PAGE_SIZE;
    return sorted.slice(start, start + AgenteListComponent.PAGE_SIZE);
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.filteredOrders.length / AgenteListComponent.PAGE_SIZE),
    );
  }

  get hasOrders(): boolean {
    return this.orders.length > 0;
  }

  /** Partial-orders count — surfaced as a counter chip in the header. */
  get partialCount(): number {
    return this.orders.filter((o) => o.or_partial === 'S').length;
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  isPartial(order: AgentOrders): boolean {
    return order.or_partial === 'S';
  }

  /**
   * Priority badge variant for the SCSS, derived from the backend code.
   * Falls back to `default` when the code is unknown — keeps the UI
   * safe against future code additions.
   */
  priorityVariant(order: AgentOrders): 'alta' | 'media' | 'bassa' | 'default' {
    switch (order.tab_order_priority_cod) {
      case 0:
      case 1:
        return 'alta';
      case 2:
        return 'media';
      case 3:
        return 'bassa';
      default:
        return 'default';
    }
  }

  /** Status-progress percentage clamped to [0, 100] for the bar fill. */
  progress(order: AgentOrders): number {
    const raw =
      order.tab_order_status_cod === 100
        ? order.tab_order_status_previous_perc
        : order.tab_order_status_perc;
    const n = Number(raw ?? 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  }

  /** Format a date-ish string to dd/MM/yyyy. */
  formatDate(value: string | number | null | undefined): string {
    if (!value) return '—';
    // The backend sentinel "0001-01-01T00:00:00" means "no date".
    if (String(value).startsWith('0001-01-01')) return '—';
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Actions.
  // ──────────────────────────────────────────────────────────────────

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  onSearchInput(): void {
    this.currentPage = 0;
  }

  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.currentPage = 0;
  }

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage -= 1;
  }
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage += 1;
  }

  /**
   * Open the shipment-tracking widget for a single order.
   *
   * 🚨 FIX v2.11.1 (#13 prod): the `track_ID` input of the tracking widget
   * is semantically misnamed — at the backend level it's used as `orderId`
   * (`/get_agent_shipments_status/{orderId}/{agent_id}`). The previous
   * implementation passed `os_tracking_number`, which is the carrier
   * tracking string (often empty or alphanumeric like "DHL12345") and
   * never matched any record on the BE → every agent saw "Nessuna
   * informazione di tracking disponibile". We pass `order_id` so the
   * shipment lookup hits the correct record.
   */
  openTracking(order: AgentOrders): void {
    this.trackingTrackId = String(order.order_id ?? '');
    this.trackingAgentId = order.or_agent_id ?? '';
    this.showTracking = true;
  }

  closeTracking(): void {
    this.showTracking = false;
  }

  /** Open the order-details widget for a single order. */
  openOrderDetails(order: AgentOrders): void {
    this.detailsAgentId = order.or_agent_id ?? '';
    this.detailsOrderId = order.order_id;
    this.detailsOrderNumber = order.or_order_number ?? '';
    this.detailsGammaId = order.or_gamma_id ?? 0;
    this.detailsAssignedDate = String(order.or_assigned_date ?? '');
    this.detailsStatusCod = order.tab_order_status_cod ?? 0;
    // Pass the raw token — Angular HttpClient URL-encodes on
    // serialization. Pre-encoding here caused the double-encode bug
    // we fixed in /accesso-funzionari.openOrders.
    this.detailsTokenAgent = this.agent_token;
    this.showOrderDetails = true;
  }

  closeOrderDetails(): void {
    this.showOrderDetails = false;
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadOrders(token: string): void {
    if (!token) return;
    this.loading = true;
    this.agentsService
      .getAgentOrders(token)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (data: AgentOrders[] | null | undefined) => {
          // Backend may return an empty array (no orders) or null/undef
          // (some error states); both collapse to "no orders".
          this.orders = Array.isArray(data) ? data : [];
          // Clamp page if the new list shrunk past the current page.
          if (this.currentPage >= this.totalPages) {
            this.currentPage = this.totalPages - 1;
          }
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('agents/orders: load failed', err),
      });
  }

  /**
   * Manual server-side search (used when the client-side filter is
   * insufficient — currently we only do client-side, but the endpoint
   * is left here so we can switch over if the dataset grows).
   */
  searchOrders(): void {
    if (!this.agent_token) return;
    this.loading = true;
    this.agentsService
      .searchAgentOrders(this.searchText, this.agent_token)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result: IWsResult) => {
          this.orders = Array.isArray(result?.data) ? result.data : [];
          this.cdr.markForCheck();
        },
        error: (err) => Log.error('agents/orders: search failed', err),
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Private helpers.
  // ──────────────────────────────────────────────────────────────────

  private haystack(order: AgentOrders): string {
    return [
      order.or_order_number,
      order.or_customer,
      order.or_quote_number,
      order.ag_first_name,
      order.ag_last_name,
      order.ac_first_name,
      order.ac_last_name,
      order.or_address,
      order.tab_order_status_desc,
      order.tab_order_priority_desc,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }
  
}
